import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import type { Work, RelatedWork } from "@/api/types";

interface WorkNode extends d3.SimulationNodeDatum {
  id: string;
  title: string;
  isCenter: boolean;
  relationType?: string;
  relationLabel?: string;
}

interface WorkLink extends d3.SimulationLinkDatum<WorkNode> {
  relationType: string;
  relationLabel: string;
}

interface WorkRelationsGraphProps {
  work: Work;
  onNodeClick?: (workId: string) => void;
}

// Relation type colors with gradients
const RELATION_COLORS: Record<string, { primary: string; secondary: string; glow: string }> = {
  adaptation: { primary: "#8B5CF6", secondary: "#A78BFA", glow: "rgba(139, 92, 246, 0.6)" },
  sequel: { primary: "#3B82F6", secondary: "#60A5FA", glow: "rgba(59, 130, 246, 0.6)" },
  prequel: { primary: "#06B6D4", secondary: "#22D3EE", glow: "rgba(6, 182, 212, 0.6)" },
  part_of: { primary: "#10B981", secondary: "#34D399", glow: "rgba(16, 185, 129, 0.6)" },
  derivative: { primary: "#F59E0B", secondary: "#FBBF24", glow: "rgba(245, 158, 11, 0.6)" },
  remake: { primary: "#EF4444", secondary: "#F87171", glow: "rgba(239, 68, 68, 0.6)" },
  inspired_by: { primary: "#EC4899", secondary: "#F472B6", glow: "rgba(236, 72, 153, 0.6)" },
  based_on: { primary: "#8B5CF6", secondary: "#C4B5FD", glow: "rgba(139, 92, 246, 0.6)" },
  related: { primary: "#6B7280", secondary: "#9CA3AF", glow: "rgba(107, 114, 128, 0.6)" },
};

const DEFAULT_COLOR = {
  primary: "#6B7280",
  secondary: "#9CA3AF",
  glow: "rgba(107, 114, 128, 0.6)",
};

const getRelationColor = (type: string | undefined) => {
  if (!type) return DEFAULT_COLOR;
  return RELATION_COLORS[type] ?? DEFAULT_COLOR;
};

export function WorkRelationsGraph({ work, onNodeClick }: WorkRelationsGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: Math.max(500, rect.height) });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // Build graph data
  const buildGraphData = useCallback(() => {
    const nodes: WorkNode[] = [
      {
        id: work.id,
        title: work.preferredTitle,
        isCenter: true,
      },
    ];

    const links: WorkLink[] = [];

    work.relatedWorks.forEach((related: RelatedWork) => {
      nodes.push({
        id: related.id,
        title: related.title,
        isCenter: false,
        relationType: related.relationType,
        relationLabel: related.relationLabel,
      });

      links.push({
        source: work.id,
        target: related.id,
        relationType: related.relationType,
        relationLabel: related.relationLabel,
      });
    });

    return { nodes, links };
  }, [work]);

  // D3 visualization
  useEffect(() => {
    if (!svgRef.current || work.relatedWorks.length === 0) return;

    const { nodes, links } = buildGraphData();
    const { width, height } = dimensions;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current);

    // Add defs for gradients and filters
    const defs = svg.append("defs");

    // Glow filter for center node
    const glowFilter = defs
      .append("filter")
      .attr("id", "glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");
    glowFilter.append("feGaussianBlur").attr("stdDeviation", "4").attr("result", "coloredBlur");
    const feMerge = glowFilter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Stronger glow for hover
    const hoverGlow = defs
      .append("filter")
      .attr("id", "hoverGlow")
      .attr("x", "-100%")
      .attr("y", "-100%")
      .attr("width", "300%")
      .attr("height", "300%");
    hoverGlow.append("feGaussianBlur").attr("stdDeviation", "8").attr("result", "coloredBlur");
    const hoverMerge = hoverGlow.append("feMerge");
    hoverMerge.append("feMergeNode").attr("in", "coloredBlur");
    hoverMerge.append("feMergeNode").attr("in", "coloredBlur");
    hoverMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Gradient for each relation type
    Object.entries(RELATION_COLORS).forEach(([type, colors]) => {
      const gradient = defs
        .append("linearGradient")
        .attr("id", `gradient-${type}`)
        .attr("gradientUnits", "userSpaceOnUse");
      gradient.append("stop").attr("offset", "0%").attr("stop-color", colors.primary);
      gradient.append("stop").attr("offset", "100%").attr("stop-color", colors.secondary);
    });

    // Radial gradient for center node
    const centerGradient = defs
      .append("radialGradient")
      .attr("id", "centerGradient")
      .attr("cx", "30%")
      .attr("cy", "30%");
    centerGradient.append("stop").attr("offset", "0%").attr("stop-color", "#60A5FA");
    centerGradient.append("stop").attr("offset", "100%").attr("stop-color", "#2563EB");

    // Create zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on("zoom", (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        container.attr("transform", event.transform.toString());
      });

    svg.call(zoom);

    // Container for zoomable content
    const container = svg.append("g").attr("class", "zoom-container");

    // Draw animated background particles
    const particleGroup = container.append("g").attr("class", "particles");
    for (let i = 0; i < 30; i++) {
      particleGroup
        .append("circle")
        .attr("cx", Math.random() * width)
        .attr("cy", Math.random() * height)
        .attr("r", Math.random() * 2 + 0.5)
        .attr("fill", "currentColor")
        .attr("opacity", Math.random() * 0.3 + 0.1)
        .style("animation", `float ${String(Math.random() * 10 + 10)}s infinite ease-in-out`);
    }

    // Create force simulation
    const simulation = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink<WorkNode, WorkLink>(links)
          .id((d) => d.id)
          .distance(180)
          .strength(0.8)
      )
      .force("charge", d3.forceManyBody().strength(-800))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(70));

    // Draw links with animated dashes
    const linkGroup = container.append("g").attr("class", "links");

    const link = linkGroup
      .selectAll<SVGGElement, WorkLink>("g")
      .data(links)
      .join("g")
      .attr("class", "link-group");

    // Link background (wider, for hover area)
    link
      .append("path")
      .attr("class", "link-bg")
      .attr("stroke", "transparent")
      .attr("stroke-width", 20)
      .attr("fill", "none");

    // Main link line
    link
      .append("path")
      .attr("class", "link-line")
      .attr("stroke", (d) => `url(#gradient-${d.relationType})`)
      .attr("stroke-width", 3)
      .attr("fill", "none")
      .attr("stroke-linecap", "round")
      .attr("opacity", 0.7);

    // Animated dash overlay
    link
      .append("path")
      .attr("class", "link-dash")
      .attr("stroke", (d) => getRelationColor(d.relationType).secondary)
      .attr("stroke-width", 2)
      .attr("fill", "none")
      .attr("stroke-dasharray", "8 12")
      .attr("opacity", 0.5)
      .style("animation", "dashMove 2s linear infinite");

    // Link labels
    const linkLabels = container.append("g").attr("class", "link-labels");

    const linkLabel = linkLabels
      .selectAll<SVGGElement, WorkLink>("g")
      .data(links)
      .join("g")
      .attr("class", "link-label");

    linkLabel
      .append("rect")
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("fill", "#1F2937")
      .attr("stroke", (d) => getRelationColor(d.relationType).primary)
      .attr("stroke-width", 1)
      .attr("opacity", 0);

    linkLabel
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "#E5E7EB")
      .attr("font-size", 11)
      .attr("font-weight", 500)
      .attr("opacity", 0)
      .text((d) => d.relationLabel);

    // Draw nodes
    const nodeGroup = container.append("g").attr("class", "nodes");

    const node = nodeGroup
      .selectAll<SVGGElement, WorkNode>("g")
      .data(nodes)
      .join("g")
      .attr("class", "node")
      .attr("cursor", "pointer");

    // Add drag behavior
    const dragBehavior = d3
      .drag<SVGGElement, WorkNode>()
      .on("start", (event: d3.D3DragEvent<SVGGElement, WorkNode, WorkNode>, d: WorkNode) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event: d3.D3DragEvent<SVGGElement, WorkNode, WorkNode>, d: WorkNode) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event: d3.D3DragEvent<SVGGElement, WorkNode, WorkNode>, d: WorkNode) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    node.call(dragBehavior);

    // Node outer glow ring (animated) - only for center
    node
      .filter((d) => d.isCenter)
      .append("circle")
      .attr("class", "pulse-ring")
      .attr("r", 55)
      .attr("fill", "none")
      .attr("stroke", "#3B82F6")
      .attr("stroke-width", 2)
      .attr("opacity", 0.3)
      .style("animation", "pulse 2s ease-out infinite");

    // Node glow background
    node
      .append("circle")
      .attr("class", "node-glow")
      .attr("r", (d) => (d.isCenter ? 45 : 35))
      .attr("fill", (d) => (d.isCenter ? "#3B82F6" : getRelationColor(d.relationType).primary))
      .attr("opacity", 0.2)
      .attr("filter", "url(#glow)");

    // Main node circle
    node
      .append("circle")
      .attr("class", "node-circle")
      .attr("r", (d) => (d.isCenter ? 40 : 30))
      .attr("fill", (d) =>
        d.isCenter ? "url(#centerGradient)" : getRelationColor(d.relationType).primary
      )
      .attr("stroke", (d) => (d.isCenter ? "#60A5FA" : getRelationColor(d.relationType).secondary))
      .attr("stroke-width", 3)
      .attr("filter", "url(#glow)");

    // Inner highlight
    node
      .append("circle")
      .attr("class", "node-highlight")
      .attr("r", (d) => (d.isCenter ? 30 : 22))
      .attr("fill", "none")
      .attr("stroke", "rgba(255, 255, 255, 0.3)")
      .attr("stroke-width", 2)
      .attr("transform", "translate(-3, -3)");

    // Node icon
    node
      .append("text")
      .attr("class", "node-icon")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-size", (d) => (d.isCenter ? 20 : 16))
      .attr("fill", "white")
      .text((d) => (d.isCenter ? "★" : "◆"));

    // Node labels
    const labelGroup = container.append("g").attr("class", "labels");

    const label = labelGroup
      .selectAll<SVGGElement, WorkNode>("g")
      .data(nodes)
      .join("g")
      .attr("class", "label");

    // Label background
    label
      .append("rect")
      .attr("rx", 6)
      .attr("ry", 6)
      .attr("fill", "#111827")
      .attr("stroke", (d) => (d.isCenter ? "#3B82F6" : getRelationColor(d.relationType).primary))
      .attr("stroke-width", 1.5)
      .attr("opacity", 0.95);

    // Label text
    label
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "white")
      .attr("font-size", (d) => (d.isCenter ? 14 : 12))
      .attr("font-weight", (d) => (d.isCenter ? 700 : 500))
      .text((d) => (d.title.length > 25 ? d.title.slice(0, 22) + "..." : d.title));

    // Measure and position label backgrounds
    label.each(function () {
      const group = d3.select(this);
      const textEl = group.select("text").node() as SVGTextElement | null;
      if (textEl) {
        const bbox = textEl.getBBox();
        group
          .select("rect")
          .attr("x", bbox.x - 10)
          .attr("y", bbox.y - 6)
          .attr("width", bbox.width + 20)
          .attr("height", bbox.height + 12);
      }
    });

    // Interaction handlers
    node.on("mouseenter", function (_event: MouseEvent, d: WorkNode) {
      setHoveredNode(d.id);

      d3.select(this)
        .select(".node-circle")
        .transition()
        .duration(200)
        .attr("r", d.isCenter ? 48 : 38)
        .attr("filter", "url(#hoverGlow)");

      d3.select(this)
        .select(".node-glow")
        .transition()
        .duration(200)
        .attr("r", d.isCenter ? 55 : 45)
        .attr("opacity", 0.4);

      // Show link labels for connected links
      const nodeId = d.id;
      const isConnected = (l: WorkLink) => {
        // After simulation, source/target are WorkNode objects
        const source = l.source as WorkNode;
        const target = l.target as WorkNode;
        return source.id === nodeId || target.id === nodeId;
      };

      linkLabel.filter(isConnected).select("rect").transition().duration(200).attr("opacity", 0.95);
      linkLabel.filter(isConnected).select("text").transition().duration(200).attr("opacity", 1);

      // Highlight connected links
      link
        .filter(isConnected)
        .select(".link-line")
        .transition()
        .duration(200)
        .attr("stroke-width", 5)
        .attr("opacity", 1);
    });

    node.on("mouseleave", function (_event: MouseEvent, d: WorkNode) {
      setHoveredNode(null);

      d3.select(this)
        .select(".node-circle")
        .transition()
        .duration(200)
        .attr("r", d.isCenter ? 40 : 30)
        .attr("filter", "url(#glow)");

      d3.select(this)
        .select(".node-glow")
        .transition()
        .duration(200)
        .attr("r", d.isCenter ? 45 : 35)
        .attr("opacity", 0.2);

      // Hide link labels
      linkLabel.select("rect").transition().duration(200).attr("opacity", 0);
      linkLabel.select("text").transition().duration(200).attr("opacity", 0);

      // Reset link styles
      link
        .select(".link-line")
        .transition()
        .duration(200)
        .attr("stroke-width", 3)
        .attr("opacity", 0.7);
    });

    node.on("click", (event: MouseEvent, d: WorkNode) => {
      event.stopPropagation();
      if (onNodeClick && !d.isCenter) {
        onNodeClick(d.id);
      }
    });

    // Update positions on simulation tick
    simulation.on("tick", () => {
      // Update link paths with curves
      link.selectAll<SVGPathElement, WorkLink>("path").attr("d", (d) => {
        const source = d.source as WorkNode;
        const target = d.target as WorkNode;
        const sourceX = source.x ?? 0;
        const sourceY = source.y ?? 0;
        const targetX = target.x ?? 0;
        const targetY = target.y ?? 0;

        const dx = targetX - sourceX;
        const dy = targetY - sourceY;
        const dr = Math.sqrt(dx * dx + dy * dy) * 0.8;

        return `M${String(sourceX)},${String(sourceY)}A${String(dr)},${String(dr)} 0 0,1 ${String(targetX)},${String(targetY)}`;
      });

      // Update link labels position
      linkLabel.attr("transform", (d) => {
        const source = d.source as WorkNode;
        const target = d.target as WorkNode;
        const sourceX = source.x ?? 0;
        const sourceY = source.y ?? 0;
        const targetX = target.x ?? 0;
        const targetY = target.y ?? 0;
        const midX = (sourceX + targetX) / 2;
        const midY = (sourceY + targetY) / 2;
        return `translate(${String(midX)}, ${String(midY)})`;
      });

      // Update node positions
      node.attr("transform", (d) => `translate(${String(d.x ?? 0)}, ${String(d.y ?? 0)})`);

      // Update label positions (below nodes)
      label.attr(
        "transform",
        (d) => `translate(${String(d.x ?? 0)}, ${String((d.y ?? 0) + (d.isCenter ? 60 : 50))})`
      );
    });

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [work, dimensions, buildGraphData, onNodeClick]);

  // If no relations, show empty state
  if (work.relatedWorks.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Aucune relation</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Cette œuvre n'a pas encore de relations avec d'autres œuvres.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative h-[600px] w-full overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:border-gray-700"
    >
      {/* Animated background grid */}
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
          `,
            backgroundSize: "40px 40px",
            animation: "gridMove 20s linear infinite",
          }}
        />
      </div>

      {/* Corner decorations */}
      <div className="pointer-events-none absolute top-4 left-4 h-20 w-20 rounded-tl-xl border-t-2 border-l-2 border-blue-500/30" />
      <div className="pointer-events-none absolute top-4 right-4 h-20 w-20 rounded-tr-xl border-t-2 border-r-2 border-blue-500/30" />
      <div className="pointer-events-none absolute bottom-4 left-4 h-20 w-20 rounded-bl-xl border-b-2 border-l-2 border-blue-500/30" />
      <div className="pointer-events-none absolute right-4 bottom-4 h-20 w-20 rounded-br-xl border-r-2 border-b-2 border-blue-500/30" />

      {/* Title overlay */}
      <div className="absolute top-6 left-6 z-10">
        <h3 className="text-lg font-bold text-white">Réseau des œuvres</h3>
        <p className="text-sm text-gray-400">
          {work.relatedWorks.length} relation{work.relatedWorks.length > 1 ? "s" : ""}
        </p>
      </div>

      {/* Legend */}
      <div className="absolute top-6 right-6 z-10 rounded-lg bg-gray-900/80 p-3 backdrop-blur-sm">
        <div className="space-y-2 text-xs">
          {Array.from(new Set(work.relatedWorks.map((r) => r.relationType))).map((type) => (
            <div key={type} className="flex items-center gap-2">
              <div
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: getRelationColor(type).primary }}
              />
              <span className="text-gray-300 capitalize">{type.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute right-6 bottom-6 z-10 flex gap-2">
        <button
          type="button"
          className="rounded-lg bg-gray-800/80 p-2 text-gray-400 backdrop-blur-sm transition hover:bg-gray-700 hover:text-white"
          title="Réinitialiser la vue"
          onClick={() => {
            if (svgRef.current) {
              const svg = d3.select(svgRef.current);
              const zoomBehavior = d3.zoom<SVGSVGElement, unknown>();
              // Use bound transform function to avoid unbound method issue
              const transformFn = zoomBehavior.transform.bind(zoomBehavior);
              svg.transition().duration(500).call(transformFn, d3.zoomIdentity);
            }
          }}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Hovered node info */}
      {hoveredNode && hoveredNode !== work.id && (
        <div className="absolute bottom-6 left-6 z-10 rounded-lg bg-gray-900/90 p-4 backdrop-blur-sm">
          <p className="text-sm font-medium text-white">
            {work.relatedWorks.find((r) => r.id === hoveredNode)?.title ?? ""}
          </p>
          <p className="mt-1 text-xs text-gray-400">Cliquez pour voir les détails</p>
        </div>
      )}

      {/* SVG container */}
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="cursor-grab active:cursor-grabbing"
      />

      {/* CSS animations */}
      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.3);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }

        @keyframes dashMove {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -40;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes gridMove {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(40px, 40px);
          }
        }
      `}</style>
    </div>
  );
}
